import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const UserPrefsContext = createContext(null);

const DEFAULTS = {
  home_screen: 'welcome',                // 'today' | 'welcome'
  today_completed_behavior: 'move',      // 'move'  | 'hide'
  daily_quote_enabled: true,             // gentle daily quote push
  daily_quote_time: '08:00',             // HH:MM in user's local timezone
  streak_save_enabled: true,             // evening streak-save invite
  timezone: 'UTC',                       // IANA timezone, populated client-side
  widget_style: 'habits',                // 'habits' | 'quote' — iOS home-screen widget layout
  widget_coach_dismissed: false,         // user has dismissed the "add widget" coach
};

export const UserPrefsProvider = ({ children }) => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [schemaReady, setSchemaReady] = useState(true);
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!user) { setPrefs(DEFAULTS); setLoaded(false); setSchemaReady(true); return; }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        const msg = (error.message || '').toLowerCase();
        if (msg.includes('column') || msg.includes('does not exist')) {
          setSchemaReady(false);
        }
      }
      // Detect the browser's IANA timezone and persist it if missing/UTC.
      let detectedTz = DEFAULTS.timezone;
      try { detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (_) { /* keep default */ }
      const storedTz = data?.timezone;
      const tz = storedTz && storedTz !== 'UTC' ? storedTz : detectedTz;
      setPrefs({
        home_screen: data?.home_screen || DEFAULTS.home_screen,
        today_completed_behavior: data?.today_completed_behavior || DEFAULTS.today_completed_behavior,
        daily_quote_enabled: data?.daily_quote_enabled ?? DEFAULTS.daily_quote_enabled,
        daily_quote_time: data?.daily_quote_time || DEFAULTS.daily_quote_time,
        streak_save_enabled: data?.streak_save_enabled ?? DEFAULTS.streak_save_enabled,
        timezone: tz,
        widget_style: data?.widget_style || DEFAULTS.widget_style,
        widget_coach_dismissed: data?.widget_coach_dismissed ?? DEFAULTS.widget_coach_dismissed,
      });
      setLoaded(true);
      // Persist a detected timezone if the stored value was missing/UTC and we
      // discovered something better. Fire-and-forget — no toast.
      if ((!storedTz || storedTz === 'UTC') && detectedTz && detectedTz !== 'UTC') {
        supabase.from('profiles').update({ timezone: detectedTz }).eq('id', user.id).then(() => {});
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Optimistic update. Schema-not-migrated case shows a one-time toast.
  const setPref = useCallback(async (key, value) => {
    if (!user) return { error: { message: 'Not signed in' } };
    if (!(key in DEFAULTS)) return { error: { message: `Unknown pref: ${key}` } };
    setPrefs((p) => ({ ...p, [key]: value }));
    if (!schemaReady) {
      if (!warnedRef.current) {
        warnedRef.current = true;
        toast.message('Preview only', {
          description: 'Run supabase_phase4.sql in your Supabase SQL editor to persist customization preferences.',
          duration: 6000,
        });
      }
      return { error: null };
    }
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [key]: value })
        .eq('id', user.id);
      if (error) {
        const msg = (error.message || '').toLowerCase();
        if (msg.includes('column') || msg.includes('does not exist') || msg.includes('schema')) {
          setSchemaReady(false);
          if (!warnedRef.current) {
            warnedRef.current = true;
            toast.message('Preview only', {
              description: 'Run supabase_phase4.sql in your Supabase SQL editor to persist customization preferences.',
              duration: 6000,
            });
          }
        }
        return { error };
      }
      return { error: null };
    } catch (e) {
      // Network or transient supabase-js error — keep optimistic state, no toast spam.
      return { error: { message: e?.message || 'Update failed' } };
    }
  }, [user, schemaReady]);

  return (
    <UserPrefsContext.Provider value={{ prefs, setPref, loaded, schemaReady }}>
      {children}
    </UserPrefsContext.Provider>
  );
};

export const useUserPrefs = () => {
  const ctx = useContext(UserPrefsContext);
  if (!ctx) throw new Error('useUserPrefs must be used inside UserPrefsProvider');
  return ctx;
};
